import type { ReactNode } from "react";
import { ActionIcon, Button, Checkbox, Select, Text } from "@mantine/core";
import { IconSortAscending, IconSortDescending } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { MobileView } from "./types";

/** Loading / empty placeholder; returns null when there are rows to show. */
export function ListStatus<T>({ view }: { view: MobileView<T> }) {
  const { t } = useTranslation();
  if (view.loading) return <Text c="dimmed" py="sm">{t("common.loading")}</Text>;
  if (view.rows.length === 0) {
    const e: ReactNode = view.empty ?? t("table.empty");
    return typeof e === "string" || typeof e === "number" ? <Text c="dimmed" py="sm">{e}</Text> : <>{e}</>;
  }
  return null;
}

/** Selection checkbox inside a ≥40px hit area; never propagates the tap to the row. */
export function RowCheckbox<T>({ view, row }: { view: MobileView<T>; row: T }) {
  const { t } = useTranslation();
  if (!view.selection) return null;
  const sel = view.selection;
  if (sel.selectable && !sel.selectable(row)) return null;
  return (
    <div
      className="app-rt-check"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Checkbox checked={sel.isSelected(row)} onChange={() => sel.toggle(row)} aria-label={t("table.select")} />
    </div>
  );
}

/** Label/value grid of the visible fields. */
export function FieldGrid<T>({ view, row }: { view: MobileView<T>; row: T }) {
  const visible = view.fields.filter((f) => !f.hidden?.(row));
  if (!visible.length) return null;
  return (
    <div className="app-rt-fields">
      {visible.map((f, i) => (
        <div key={i} className="app-rt-field">
          <Text size="xs" c="dimmed" lh={1.3}>{f.label}</Text>
          <div className="app-rt-value">{f.render(row)}</div>
        </div>
      ))}
    </div>
  );
}

/** Button row: „Deschide” first (when onOpen), then the row's own actions. */
export function ActionRow<T>({ view, row }: { view: MobileView<T>; row: T }) {
  const { t } = useTranslation();
  const extra = view.actions?.(row);
  if (!view.onOpen && (extra == null || extra === false)) return null;
  const open = view.onOpen;
  return (
    <div className="app-rt-actions" onClick={(e) => e.stopPropagation()}>
      {open && (
        <Button variant="light" size="sm" className="app-rt-open" onClick={() => open(row)}>
          {t("table.open")}
        </Button>
      )}
      {extra}
    </div>
  );
}

/** Row tint (often translucent, e.g. green-light); `onSurface` layers it over the theme's card surface. */
export function tintStyle(bg: string | undefined, onSurface = false) {
  if (!bg) return undefined;
  return { background: onSurface ? `linear-gradient(${bg}, ${bg}), var(--app-surface, var(--mantine-color-body))` : bg };
}

/** „Select all” row above the list; only when the view provides `selection.toggleAll`. */
export function SelectAllRow<T>({ view }: { view: MobileView<T> }) {
  const { t } = useTranslation();
  const sel = view.selection;
  if (!sel?.toggleAll) return null;
  return (
    <div className="app-rt-selectall" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <Checkbox
        checked={!!sel.allSelected}
        indeterminate={!sel.allSelected && !!sel.someSelected}
        onChange={() => sel.toggleAll?.()}
        label={t("table.selectAll")}
      />
    </div>
  );
}

/** Sort field + direction above the list; only when the view provides `sort` (desktop sorts via headers). */
export function SortBar<T>({ view }: { view: MobileView<T> }) {
  const { t } = useTranslation();
  const sort = view.sort;
  if (!sort || sort.options.length === 0) return null;
  const asc = sort.dir === "asc";
  const dirLabel = asc ? t("table.sortAsc") : t("table.sortDesc");
  return (
    <div className="app-rt-sort">
      <Select
        className="app-rt-sort-field"
        data={sort.options}
        value={sort.value}
        onChange={(v) => v && sort.onChange(v, sort.dir)}
        allowDeselect={false}
        placeholder={t("table.sortBy")}
        aria-label={t("table.sortBy")}
      />
      <ActionIcon
        variant="default"
        size={40}
        aria-label={`${t("table.sortBy")}: ${dirLabel}`}
        title={dirLabel}
        onClick={() => sort.onChange(sort.value ?? sort.options[0].value, asc ? "desc" : "asc")}
      >
        {asc ? <IconSortAscending size={18} /> : <IconSortDescending size={18} />}
      </ActionIcon>
    </div>
  );
}
